import { useMemo } from 'react'
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  queryOptions,
  type QueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query'
import { COMPANIES_PAGE_SIZE } from '@/constants/api'
import { companiesKeys, adminKeys } from '@/hooks/query-keys'
import * as companiesService from '@/services/companies.service'
import {
  OVERVIEW_ACTUAL_VS_BUDGET_METRICS,
  type CompanyActualVsBudgetMetric,
} from '@/features/company-overview/actual-vs-budget'
import type {
  Company,
  CompanyList,
  CompanySearch,
  CreateCompany,
  UpdateCompany,
  Filter,
  Sort,
} from '@/types/api'

// Re-export companiesKeys for consumers that import from this file
export { companiesKeys } from '@/hooks/query-keys'

type CompanySearchData = InfiniteData<CompanyList>

function isCompaniesSearchQueryKey(queryKey: QueryKey): boolean {
  return (
    Array.isArray(queryKey) &&
    queryKey.length >= 2 &&
    queryKey[0] === companiesKeys.all[0] &&
    queryKey[1] === 'search'
  )
}

function patchCompanyInSearchData(
  old: CompanySearchData | undefined,
  id: string,
  updater: (company: Company) => Company
): CompanySearchData | undefined {
  if (!old?.pages) return old

  let updated = false
  const newPages = old.pages.map((page) => {
    let pageUpdated = false
    const newData = page.data.map((company) => {
      if (company.id === id) {
        pageUpdated = true
        updated = true
        return updater(company)
      }
      return company
    })
    return pageUpdated ? { ...page, data: newData } : page
  })

  if (!updated) return old

  return { ...old, pages: newPages }
}

function getActiveCompaniesSearchQueryKeys(
  queryClient: QueryClient
): QueryKey[] {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: companiesKeys.all, type: 'active' })
    .filter((query) => isCompaniesSearchQueryKey(query.queryKey))
    .map((query) => query.queryKey)
}

function getActiveCompaniesSearchPreviousData(
  queryClient: QueryClient,
  queryKeys: QueryKey[]
) {
  return queryKeys.map(
    (queryKey) =>
      [queryKey, queryClient.getQueryData<CompanySearchData>(queryKey)] as const
  )
}

function patchActiveCompaniesSearchQueries(
  queryClient: QueryClient,
  queryKeys: QueryKey[],
  updater: (old: CompanySearchData | undefined) => CompanySearchData | undefined
) {
  for (const queryKey of queryKeys) {
    queryClient.setQueryData<CompanySearchData>(queryKey, updater)
  }
}

// =============================================================================
// Query Options
// =============================================================================

const COMPANIES_STALE_TIME = 30000 // 30 seconds
const COMPANIES_GC_TIME = 300000 // 5 minutes

export const companyQueryOptions = (id: string | null) =>
  queryOptions({
    queryKey: companiesKeys.detail(id ?? ''),
    queryFn: () => (id ? companiesService.getCompany(id) : null),
    enabled: !!id,
    staleTime: COMPANIES_STALE_TIME,
    gcTime: COMPANIES_GC_TIME,
  })

// =============================================================================
// Hooks
// =============================================================================

interface UseCompaniesSearchParams {
  viewId?: string
  filters?: Filter[]
  sorts?: Sort[]
  limit?: number
  /**
   * Defer the query until the caller has finished hydrating filters/sorts
   * (e.g. URL sync + view-derived defaults). Skips a redundant first fetch
   * that fires before the working state is ready.
   */
  enabled?: boolean
}

/**
 * Hook to search companies with infinite scroll pagination
 */
export function useCompaniesSearch({
  viewId,
  filters = [],
  sorts = [],
  limit = COMPANIES_PAGE_SIZE,
  enabled = true,
}: UseCompaniesSearchParams) {
  // Always include filters/sorts in the query key for proper cache invalidation
  // But only send them to the API when not relying on view's saved config
  const shouldUseViewFilters =
    viewId && filters.length === 0 && sorts.length === 0

  // Query key always includes filters/sorts for cache differentiation
  const queryKeyParams: Omit<CompanySearch, 'cursor'> = {
    limit,
    viewId,
    filters,
    sorts,
  }

  // API params may omit filters/sorts when using view's saved config
  const apiParams: Omit<CompanySearch, 'cursor'> = {
    limit,
    viewId,
    ...(shouldUseViewFilters ? {} : { filters, sorts }),
  }

  const query = useInfiniteQuery({
    queryKey: companiesKeys.search(queryKeyParams),
    queryFn: async ({ pageParam }) => {
      const result = await companiesService.searchCompanies({
        ...apiParams,
        cursor: pageParam,
      })
      return result
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore || !lastPage.nextCursor) return undefined
      // nextCursor from API could be string or object, ensure we return string
      const cursor = lastPage.nextCursor
      return typeof cursor === 'string' ? cursor : undefined
    },
    enabled,
    staleTime: COMPANIES_STALE_TIME,
    gcTime: COMPANIES_GC_TIME,
  })

  // Flatten pages into single data array (memoized to keep a stable reference)
  const data = useMemo(() => {
    return query.data?.pages.flatMap((page) => page.data) ?? []
  }, [query.data?.pages])

  return {
    data,
    isLoading: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    hasMore: query.hasNextPage ?? false,
    fetchMore: query.fetchNextPage,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch a single company by ID
 */
export function useCompany(id: string | null) {
  return useQuery(companyQueryOptions(id))
}

export function useCompanyActualVsBudget(
  companyId: string,
  enabled: boolean,
  metrics: readonly CompanyActualVsBudgetMetric[] = OVERVIEW_ACTUAL_VS_BUDGET_METRICS
) {
  return useQuery({
    queryKey: companiesKeys.actualVsBudget(companyId, metrics),
    queryFn: () =>
      companiesService.getCompanyActualVsBudget(companyId, metrics),
    enabled: Boolean(companyId) && enabled,
    staleTime: COMPANIES_STALE_TIME,
    gcTime: COMPANIES_GC_TIME,
  })
}

/**
 * Hook to fetch recent transactions for a company.
 */
export function useCompanyTransactions(
  companyId: string | null,
  params: companiesService.GetCompanyTransactionsParams = {}
) {
  return useQuery({
    queryKey: companiesKeys.transactions(companyId ?? '', params),
    queryFn: () =>
      companyId
        ? companiesService.getCompanyTransactions(companyId, params)
        : null,
    enabled: !!companyId,
    staleTime: COMPANIES_STALE_TIME,
    gcTime: COMPANIES_GC_TIME,
  })
}

/**
 * Hook to create a new company
 */
export function useCreateCompany() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCompany) => companiesService.createCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companiesKeys.all })
    },
  })
}

/**
 * Hook to update an existing company (PATCH accepts partial data)
 * Supports optimistic updates for all field changes.
 */
export function useUpdateCompany() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Partial<UpdateCompany>
      /** Optional location payload used when optimistic cache patching is enabled */
      location?: {
        id?: string
        country?: { id: string; name: string } | null
        state?: { id: string; name: string } | null
        city?: { id: string; name: string } | null
      }
      /** Set false when UI already has local optimistic state */
      optimistic?: boolean
    }) => companiesService.updateCompany(id, data),
    onMutate: async ({ id, data, location, optimistic = true }) => {
      if (!optimistic) {
        await queryClient.cancelQueries({
          queryKey: [...companiesKeys.all, 'search'],
          type: 'active',
        })
        return { previousData: [], optimisticApplied: false }
      }

      await queryClient.cancelQueries({ queryKey: companiesKeys.all })
      const activeSearchQueries = getActiveCompaniesSearchQueryKeys(queryClient)
      const previousData = getActiveCompaniesSearchPreviousData(
        queryClient,
        activeSearchQueries
      )

      patchActiveCompaniesSearchQueries(
        queryClient,
        activeSearchQueries,
        (old) =>
          patchCompanyInSearchData(old, id, (company) => {
            // Build optimistic update - spread all fields from data.
            // locationId won't affect the company object (it's not a valid key),
            // so location payload is handled explicitly below.
            const updates: Partial<typeof company> = { ...data }

            const sanitizedLocation = location
              ? {
                  country: location.country ?? undefined,
                  state: location.state ?? undefined,
                  city: location.city ?? undefined,
                }
              : undefined

            if ('locationId' in data) {
              updates.location =
                data.locationId === undefined ? undefined : sanitizedLocation
            }

            if ('countryId' in data || 'stateId' in data) {
              updates.location = sanitizedLocation
            }

            return { ...company, ...updates }
          })
      )

      return { previousData, optimisticApplied: true }
    },
    onError: (_, __, context) => {
      if (!context?.optimisticApplied) return
      context.previousData?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSuccess: (updatedCompany) => {
      const activeSearchQueries = getActiveCompaniesSearchQueryKeys(queryClient)
      patchActiveCompaniesSearchQueries(
        queryClient,
        activeSearchQueries,
        (old) =>
          patchCompanyInSearchData(old, updatedCompany.id, () => updatedCompany)
      )

      queryClient.setQueryData(
        companiesKeys.detail(updatedCompany.id),
        updatedCompany
      )
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: companiesKeys.all,
        type: 'inactive',
      })
      queryClient.invalidateQueries({
        queryKey: companiesKeys.detail(variables.id),
      })
      // Owner changes affect admin dashboard user assignments
      if (
        variables.data.primaryOwnerId !== undefined ||
        variables.data.secondaryOwnerId !== undefined
      ) {
        queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      }
    },
  })
}

/**
 * Hook to delete a company
 */
export function useDeleteCompany() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => companiesService.deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companiesKeys.all })
    },
  })
}

// =============================================================================
// Company Relationship Mutations
// =============================================================================

/**
 * Hook to add an industry to a company.
 * Cache is patched on success with the server response, not optimistically.
 * Callers must manage their own local optimistic state for instant UI.
 */
export function useAddCompanyIndustry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      companyId,
      industryId,
    }: {
      companyId: string
      industryId: string
      name: string
    }) => companiesService.addCompanyIndustry(companyId, industryId),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [...companiesKeys.all, 'search'],
        type: 'active',
      })
    },
    onSuccess: (createdIndustry, variables) => {
      const activeSearchQueries = getActiveCompaniesSearchQueryKeys(queryClient)
      patchActiveCompaniesSearchQueries(
        queryClient,
        activeSearchQueries,
        (old) =>
          patchCompanyInSearchData(old, variables.companyId, (company) => {
            const exists = company.industries.some(
              (i) => i.id === variables.industryId
            )
            if (exists) return company

            return {
              ...company,
              industries: [
                ...company.industries,
                {
                  id: variables.industryId,
                  name: createdIndustry.industryName ?? variables.name,
                  isPrimary: createdIndustry.isPrimary ?? false,
                },
              ],
            }
          })
      )
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: companiesKeys.all,
        type: 'inactive',
      })
      queryClient.invalidateQueries({
        queryKey: companiesKeys.detail(variables.companyId),
      })
    },
  })
}

/**
 * Hook to remove an industry from a company.
 * Cache is patched on success with the server response, not optimistically.
 * Callers must manage their own local optimistic state for instant UI.
 */
export function useRemoveCompanyIndustry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      companyId,
      industryId,
    }: {
      companyId: string
      industryId: string
    }) => companiesService.removeCompanyIndustry(companyId, industryId),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [...companiesKeys.all, 'search'],
        type: 'active',
      })
    },
    onSuccess: (_, variables) => {
      const activeSearchQueries = getActiveCompaniesSearchQueryKeys(queryClient)
      patchActiveCompaniesSearchQueries(
        queryClient,
        activeSearchQueries,
        (old) =>
          patchCompanyInSearchData(old, variables.companyId, (company) => ({
            ...company,
            industries: company.industries.filter(
              (i) => i.id !== variables.industryId
            ),
          }))
      )
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: companiesKeys.all,
        type: 'inactive',
      })
      queryClient.invalidateQueries({
        queryKey: companiesKeys.detail(variables.companyId),
      })
    },
  })
}

/**
 * Hook to add a sector to a company.
 * Cache is patched on success with the server response, not optimistically.
 * Callers must manage their own local optimistic state for instant UI.
 */
export function useAddCompanySector() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      companyId,
      sectorId,
    }: {
      companyId: string
      sectorId: string
      name: string
    }) => companiesService.addCompanySector(companyId, sectorId),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [...companiesKeys.all, 'search'],
        type: 'active',
      })
    },
    onSuccess: (createdSector, variables) => {
      const activeSearchQueries = getActiveCompaniesSearchQueryKeys(queryClient)
      patchActiveCompaniesSearchQueries(
        queryClient,
        activeSearchQueries,
        (old) =>
          patchCompanyInSearchData(old, variables.companyId, (company) => {
            const exists = company.sectors.some(
              (s) => s.id === variables.sectorId
            )
            if (exists) return company

            return {
              ...company,
              sectors: [
                ...company.sectors,
                {
                  id: variables.sectorId,
                  name: createdSector.sectorName ?? variables.name,
                  isPrimary: createdSector.isPrimary ?? false,
                },
              ],
            }
          })
      )
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: companiesKeys.all,
        type: 'inactive',
      })
      queryClient.invalidateQueries({
        queryKey: companiesKeys.detail(variables.companyId),
      })
    },
  })
}

/**
 * Hook to remove a sector from a company.
 * Cache is patched on success with the server response, not optimistically.
 * Callers must manage their own local optimistic state for instant UI.
 */
export function useRemoveCompanySector() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      companyId,
      sectorId,
    }: {
      companyId: string
      sectorId: string
    }) => companiesService.removeCompanySector(companyId, sectorId),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [...companiesKeys.all, 'search'],
        type: 'active',
      })
    },
    onSuccess: (_, variables) => {
      const activeSearchQueries = getActiveCompaniesSearchQueryKeys(queryClient)
      patchActiveCompaniesSearchQueries(
        queryClient,
        activeSearchQueries,
        (old) =>
          patchCompanyInSearchData(old, variables.companyId, (company) => ({
            ...company,
            sectors: company.sectors.filter((s) => s.id !== variables.sectorId),
          }))
      )
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: companiesKeys.all,
        type: 'inactive',
      })
      queryClient.invalidateQueries({
        queryKey: companiesKeys.detail(variables.companyId),
      })
    },
  })
}
